import { ColDef, GridApi } from 'ag-grid-community';
import { IntlShape } from 'react-intl';
import { notification } from 'antd';
import { PartnerIdentity } from './ObtPartnerDetailsTab';
import { exportMonitoringData } from '../../eim/excelUtil';
import dayjs from 'dayjs';

export const OBT_CONTACT_ROLES = ['CONTACT', 'DUNNING', 'INVOICE', 'PAD', 'REJECTION'];
export const US_MARKET_ROLES = ['DealerStmt'];
export const GFBB_MDB_OBT_NEW_CONTACT_ROLES = [
    'Prenotif', 'Interest', 'OpenItem', 'Statement', 'TransConf', 'BalConf',
];
export const US_MARKET_COMPANY_CODES = [
    'E4', 'E4A', 'N6', 'OD', 'Q0', 'BJ', 'D3', 'D1', 'O0', 'O7', 'FF', 'G0',
];

export const VALID_EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Helpers ────────────────────────────────────────────────────────────────────

export function getContactRoles(companycode: string, systemCode: string): string[] {
    if ((companycode === 'A1' || companycode === 'LY') && systemCode === 'MDB') {
        return [...OBT_CONTACT_ROLES, ...GFBB_MDB_OBT_NEW_CONTACT_ROLES];
    }
    if (US_MARKET_COMPANY_CODES.includes(companycode)) {
        return [...OBT_CONTACT_ROLES, ...US_MARKET_ROLES];
    }
    return OBT_CONTACT_ROLES;
}

export function buildContactColumns(intl: IntlShape, contactRoles: string[], canUpdate: boolean): ColDef[] {
    return [
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.partnerNo', defaultMessage: 'Partner No' }),
            field: 'partnerno', checkboxSelection: true, headerCheckboxSelection: true, minWidth: 130,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.companyCode', defaultMessage: 'Company Code' }),
            field: 'companycode', minWidth: 120,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.role', defaultMessage: 'Role' }),
            field: 'role', editable: canUpdate, cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true, cellEditorParams: { values: contactRoles, cellHeight: 30 }, minWidth: 130,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.email', defaultMessage: 'E-mail' }),
            field: 'mail', editable: canUpdate, minWidth: 200,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.source', defaultMessage: 'Source' }),
            field: 'source', minWidth: 80,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.valid', defaultMessage: 'Valid' }),
            field: 'valid', minWidth: 70,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.name1', defaultMessage: 'First Name' }),
            field: 'name1', editable: canUpdate, minWidth: 120,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.name2', defaultMessage: 'Last Name' }),
            field: 'name2', editable: canUpdate, minWidth: 120,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.department', defaultMessage: 'Department' }),
            field: 'department', editable: canUpdate, minWidth: 100,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.phone', defaultMessage: 'Phone' }),
            field: 'phone', editable: canUpdate, minWidth: 120,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.fax', defaultMessage: 'Mobile' }),
            field: 'fax', editable: canUpdate, minWidth: 120,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.status', defaultMessage: 'Status' }),
            field: 'status', minWidth: 80,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.error', defaultMessage: 'API Contacts Error' }),
            field: 'error', minWidth: 150,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.insertUser', defaultMessage: 'Insert User' }),
            field: 'anlageUser', minWidth: 110,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.insertTime', defaultMessage: 'Insert Time' }),
            field: 'anlageDatum', minWidth: 140,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.updateUser', defaultMessage: 'Update User' }),
            field: 'aenderungUser', minWidth: 110,
        },
        {
            headerName: intl.formatMessage({ id: 'obt.contacts.col.updateTime', defaultMessage: 'Update Time' }),
            field: 'aenderungDatum', minWidth: 140,
        },
    ];
}

/** Build a new row object from a source contact (for Copy Selected). */
export function buildCopiedContactRow(source: Record<string, any>, partnerIdentity: PartnerIdentity): Record<string, any> {
    return {
        id: undefined,
        partnerno: source.partnerno ?? partnerIdentity.partnerno,
        idpartner: source.idpartner ?? partnerIdentity.idpartner,
        companycode: source.companycode ?? partnerIdentity.companycode,
        role: source.role ?? '',
        mail: source.mail ?? '',
        source: 'I',
        valid: true,
        name1: source.name1 ?? '',
        name2: source.name2 ?? '',
        department: source.department ?? '',
        phone: source.phone ?? '',
        fax: source.fax ?? '',
        error: '',
    };
}

/**
 * Validate single-row selection. Returns the single selected row or null.
 * Shows appropriate error notifications if selection is invalid.
 */
export function validateSingleSelection(
    selectedRows: any[],
    intl: IntlShape,
    emptyMsgId: string, emptyMsgDefault: string,
    emptyDescId: string, emptyDescDefault: string,
    tooManyMsgId: string, tooManyMsgDefault: string,
    tooManyDescId: string, tooManyDescDefault: string,
): any | null {
    if (selectedRows.length === 0) {
        notification.error({
            message: intl.formatMessage({ id: emptyMsgId, defaultMessage: emptyMsgDefault }),
            description: intl.formatMessage({ id: emptyDescId, defaultMessage: emptyDescDefault }),
        });
        return null;
    }
    if (selectedRows.length > 1) {
        notification.error({
            message: intl.formatMessage({ id: tooManyMsgId, defaultMessage: tooManyMsgDefault }),
            description: intl.formatMessage({ id: tooManyDescId, defaultMessage: tooManyDescDefault }),
        });
        return null;
    }
    return selectedRows[0];
}

/** Export all rows from the contacts grid to an Excel file. */
export function exportContactsToExcel(api: GridApi, columnDefs: ColDef[], partnerno: string): boolean {
    const allRowData: object[] = [];
    api.forEachNode((node) => { if (node.data) allRowData.push(node.data); });
    if (allRowData.length === 0) return false;
    exportMonitoringData(
        allRowData,
        columnDefs,
        `${dayjs().format('YYYY-MM-DD_HH-mm')}-Contacts-${partnerno}.xlsx`
    );
    return true;
}
